import { createAction } from '@reduxjs/toolkit';
import { notAvailable } from 'core/common/constants';
import { normalizeError } from 'core/common/errors';
import {
  ATTRS_ANALYTICS_FLOW_NAME,
  ATTRS_ANALYTICS_FUNNEL_LOCALE,
  ATTRS_ANALYTICS_FUNNEL_NAME,
  SpanName,
} from 'core/common/observability/entities';
import { AppThunk } from 'core/common/store';
import { getFunnelName } from 'core/funnel/store/selectors';
import { getEmail } from 'core/user/store';
import { fetchUserInformation } from 'core/user/store/userProfile/actions';
import { updateSubscriptions } from 'core/user/store/userSubscriptions/actions';
import { syncUtmTags } from 'core/user/store/userUtmTags/actions';
import { OrderData } from '../../entities';
import { updateOrderId } from './paymentForm';

export const updateOrderData = createAction<OrderData>('payments/UPDATE_ORDER_DATA');

export const fetchOrderRequest =
  (emailFlow: string, params: { order_id: string; user_id: string; email?: string }): AppThunk =>
  async (dispatch, getState, context) => {
    const {
      paymentService,
      userService,
      loggingService: logger,
      authService,
      httpClient,
      subscriptionService,
      observabilitySystem,
      errorMonitoringService,
      funnelSettingsService,
    } = context;

    const { order_id, user_id } = params;

    const funnelName = getFunnelName(getState());

    const spanAttributes = {
      [ATTRS_ANALYTICS_FUNNEL_NAME]: funnelName,
      [ATTRS_ANALYTICS_FLOW_NAME]: funnelSettingsService.getFlowName(),
      [ATTRS_ANALYTICS_FUNNEL_LOCALE]: funnelSettingsService.getFunnelLocale(),
    };

    const span = observabilitySystem.startSpan(SpanName.FETCH_ORDER);
    span.setAttributes(spanAttributes);

    try {
      const authEmail = params.email || getEmail(getState());

      const { token } = await authService.authenticateByNebulaId(user_id, authEmail, order_id);

      await httpClient.setHeaders({
        User: user_id,
        'Access-Token': token,
      });

      const res = await paymentService.getOrder(order_id, params);

      const subscriptions = await subscriptionService.getSubscriptions();
      dispatch(updateSubscriptions(subscriptions));

      await dispatch(fetchUserInformation());

      await userService.update({
        settings: {
          emailFlow,
        },
      });

      const email = getEmail(getState());

      dispatch(updateOrderData(res.orderData));

      dispatch(updateOrderId(order_id));

      dispatch(syncUtmTags({ user_id, email }));

      span.succeed();
    } catch (err) {
      const error = normalizeError(err);

      span.fail(error);
      logger.error(error);

      errorMonitoringService.captureError(error, {
        tags: {
          order_id,
          get_params: location.search || notAvailable,
          error_message: error.message,
          error_reason: 'fetch_order_failed',
          funnel_name: funnelName,
        },
      });
    }
  };
